/**
 * B2AlertPanel — Seuil d'alerte du coût mensuel Backblaze B2.
 *
 * Spec 2026-07-23 (extension #2) : le Premier Admin fixe un seuil en USD ;
 * un email + une notification in-app sont envoyés quand le coût mensuel
 * estimé le dépasse (anti-spam 24h). Désactivable via la clé email_prefs
 * ``b2_cost_exceeded`` (catégorie Système).
 */
import { hasRight } from "@/components/crm/crmShared";
import { useEffect, useState } from "react";
import { toast } from "@/lib/toast";
import { AlertCircle, CheckCircle2, DollarSign, Save, Trash2, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import DeskConfirm from "@/components/crm/DeskConfirm";
import { useAuth } from "@/context/AuthContext";

const fmt = (n, digits = 4) => {
  if (n === null || n === undefined) return "—";
  return Number(n).toFixed(digits);
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

export default function B2AlertPanel() {
  const { user } = useAuth();
  const canWrite = hasRight(user, "settings.backblaze"); // iter203 — droit de la matrice (plus de premier-admin en dur)
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [state, setState] = useState(null);
  const [threshold, setThreshold] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/b2-alert/state");
      setState(data);
      setThreshold(data.threshold_usd != null ? String(data.threshold_usd) : "");
    } catch (err) {
      toast.error("Chargement impossible", { description: err.response?.data?.detail || "" });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const val = threshold.trim() === "" ? null : Number(threshold);
      if (val !== null && (!isFinite(val) || val < 0)) {
        toast.error("Le seuil doit être un nombre positif ou vide (pour désactiver)");
        setSaving(false); return;
      }
      const { data } = await api.put("/settings/b2-alert", { threshold_usd: val });
      setState(data);
      setThreshold(data.threshold_usd != null ? String(data.threshold_usd) : "");
      toast.success(val === null ? "Alerte de coût désactivée" : "Seuil d'alerte enregistré");
    } catch (err) {
      toast.error("Enregistrement impossible", { description: err.response?.data?.detail || "" });
    } finally { setSaving(false); }
  };

  const [askClear, setAskClear] = useState(false); // iter387 — remplace window.confirm
  const clear = async () => {
    setAskClear(false);
    setThreshold("");
    setSaving(true);
    try {
      const { data } = await api.put("/settings/b2-alert", { threshold_usd: null });
      setState(data);
      toast.success("Alerte de coût désactivée");
    } catch (err) {
      toast.error("Suppression impossible", { description: err.response?.data?.detail || "" });
    } finally { setSaving(false); }
  };

  if (loading || !state) return null;
  const clearDialog = <DeskConfirm open={askClear} title="Désactiver l'alerte de coût B2 ?" message="Supprimer le seuil désactivera complètement l'alerte de coût B2." confirmLabel="Supprimer le seuil" onClose={() => setAskClear(false)} onConfirm={clear} testId="b2-alert-clear-confirm" />;

  const isConfigured = state.threshold_usd != null;
  const over = state.over_threshold;
  const previewEur = threshold && !isNaN(Number(threshold))
    ? (Number(threshold) * (state.usd_to_eur || 0.93)).toFixed(4)
    : null;

  return (
    <div
      className="rounded-lg p-4 mb-4"
      style={{
        background: "#17171B",
        border: "1px solid #26262C",
        borderLeft: `3px solid ${over ? "#EF4444" : "#8B5CF6"}`,
      }}
      data-testid="b2-alert-panel"
    >
      {clearDialog}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <div className="rounded-lg flex items-center justify-center"
               style={{ width: 28, height: 28, background: "transparent", border: "1px solid #33333B", color: over ? "#EF4444" : "#8B5CF6" }}>
            <Bell size={14} />
          </div>
          <div>
            <div className="text-sm font-semibold">Seuil d&apos;alerte du coût mensuel</div>
            <div className="text-[11px] text-muted-foreground">
              Notification email + in-app dès que le coût mensuel B2 estimé dépasse ce montant. Anti-spam 24h.
            </div>
          </div>
        </div>
        {isConfigured && (
          <div
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] shrink-0"
            style={{
              background: "transparent",
              color: over ? "#EF4444" : "#22C55E",
              border: `1px solid ${over ? "rgba(239,68,68,0.5)" : "rgba(34,197,94,0.5)"}`,
            }}
            data-testid={`b2-alert-status-${over ? "over" : "ok"}`}
          >
            {over ? <AlertCircle size={12} /> : <CheckCircle2 size={12} />}
            {over ? "Seuil dépassé" : "Sous le seuil"}
          </div>
        )}
      </div>

      <div className="flex items-end gap-3 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 block">
            Seuil (USD / mois) — laisser vide pour désactiver
          </Label>
          <div className="relative">
            <DollarSign size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="number" step="0.0001" min="0"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              placeholder="Ex: 0.0500"
              className="pl-8 bg-transparent border-vertec-borderDark"
              disabled={!canWrite}
              data-testid="b2-alert-threshold-input"
            />
          </div>
          {previewEur && (
            <div className="text-[10px] text-muted-foreground mt-1" data-testid="b2-alert-eur-preview">
              ≈ {previewEur} € / mois (taux {state.usd_to_eur})
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5 bg-transparent border border-[#33333B] text-[#3B82F6] hover:bg-white/5"
            onClick={save} disabled={!canWrite || saving}
            title={canWrite ? "" : "Réservé au Premier Admin"}
            data-testid="b2-alert-save"
          >
            <Save size={13} /> Enregistrer
          </Button>
          {isConfigured && (
            <Button
              variant="outline" size="sm"
              className="gap-1.5 border-vertec-borderDark text-status-danger hover:text-status-danger"
              onClick={() => setAskClear(true)} disabled={!canWrite || saving}
              title={canWrite ? "" : "Réservé au Premier Admin"}
              data-testid="b2-alert-clear"
            >
              <Trash2 size={13} /> Désactiver
            </Button>
          )}
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-vertec-borderDark/40 flex items-center justify-between text-[11px] flex-wrap gap-2">
        <div className="text-muted-foreground" data-testid="b2-alert-current">
          Coût actuel estimé :
          <strong className="text-foreground ml-1">
            ≈ {fmt(state.current_monthly_usd)} $
          </strong>
          <span className="ml-1 text-muted-foreground">
            · {fmt(state.current_monthly_eur)} €
          </span>
          {state.breakdown && (
            <span className="ml-2 text-muted-foreground" data-testid="b2-alert-breakdown">
              (Stockage {fmt(state.breakdown.storage.usd)} $ + Egress {fmt(state.breakdown.egress.usd)} $)
            </span>
          )}
        </div>
        {state.last_alert_at && state.last_alert_status === "over" && (
          <div className="text-status-danger">
            Dernière alerte envoyée : {fmtDate(state.last_alert_at)}
          </div>
        )}
      </div>
    </div>
  );
}
